import React, { useMemo } from 'react';
import { Box, Typography, Paper, Stack, Chip } from '@mui/material';
import { getDefaultTenantId, isProductionHost } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { buildExposeDocsCurl, resolveExposePublicSurface } from './exposeTenantHonesty';

export default function TrekkerDemoTab() {
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const surface = useMemo(
    () => resolveExposePublicSurface({
      selectedTenant,
      hostTenant: getDefaultTenantId(),
      isProductionHost: isProductionHost(),
    }),
    [selectedTenant]
  );
  const exampleCurl = buildExposeDocsCurl({
    tenantId: surface.tenantId,
    publicOrigin: surface.publicOrigin,
  });

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Trekker Demo (API Expose)</Typography>
          <Typography variant="body2" color="text.secondary">
            Agentic Trekker demo endpoints proxy to the Trekker agent workflows. Use an app JWT
            with the <code>trekker:read</code> scope. Demo entities are flagged <code>is_demo</code>.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label="Scope: trekker:read" size="small" />
            <Chip label="Endpoint: /public/trekker/*" size="small" />
            <Chip label="Auth: App JWT" size="small" />
            <Chip
              label={surface.tenantId ? `Tenant: ${surface.tenantId}` : 'Tenant: session required'}
              size="small"
            />
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          Endpoints
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 3 }}>
          <li>GET <code>/public/trekker/equipment</code></li>
          <li>GET <code>/public/trekker/equipment/:id</code></li>
          <li>GET <code>/public/trekker/locations</code></li>
          <li>GET <code>/public/trekker/activities</code></li>
          <li>GET <code>/public/trekker/maintenance-schedules</code></li>
          <li>GET <code>/public/trekker/service-logs</code></li>
          <li>GET <code>/public/trekker/alerts</code></li>
          <li>GET <code>/public/trekker/reports</code></li>
          <li>GET <code>/public/trekker/parts</code></li>
          <li>GET <code>/public/trekker/suppliers</code></li>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          Quickstart
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Mint an app JWT from the Mint Token tab, then call the endpoints. Host and
          <code> x-tenant-id</code> follow the current session tenant
          {surface.tenantId ? ` (${surface.tenantId})` : ''} — not a hard-coded <code>dev</code>.
        </Typography>
        <Box
          component="pre"
          sx={{
            p: 2,
            bgcolor: 'grey.900',
            color: 'grey.100',
            borderRadius: 1,
            overflowX: 'auto',
            fontSize: 12,
          }}
        >
          {exampleCurl}
        </Box>
      </Paper>
    </Stack>
  );
}
